import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';

import toast from '@helpers/toast';

import { RootState } from '@store/reducers';

import { createDraftPackageApi, deleteAllDraftPackagesApi, deleteDraftPackageApi, updateDraftPackageApi } from './newShipment.api';
import {
  clearDraftFailure,
  clearDraftRequest,
  clearDraftSuccess,
  createDraftPackageFailure,
  createDraftPackageRequest,
  createDraftPackageSuccess,
  deleteDraftPackageFailure,
  deleteDraftPackageRequest,
  deleteDraftPackageSuccess,
  DraftPackageInput,
  DraftShipmentMeta,
  updateDraftPackageFailure,
  updateDraftPackageRequest,
  updateDraftPackageSuccess,
} from './newShipment.slice';

import { PackageStatus, ShipmentStatus } from '@stone/constants';
import { PackageDraftInput, ShipmentDraftInput } from '@stone/interfaces';
import { handleSagaError } from '@stone/sagaHelpers';

const parseErrorMessage = (error: any, fallback: string): string => String(toast.parseErrorMessage(error, fallback) ?? fallback);

interface CreatePayload {
  meta: DraftShipmentMeta;
  pkg: DraftPackageInput;
  existingShipmentId: number | null;
}

function* createDraftPackageSaga(action: PayloadAction<CreatePayload>): Generator<any, void, any> {
  const { meta, pkg, existingShipmentId } = action.payload;
  try {
    const shipmentPayload: ShipmentDraftInput = {
      shipment_id: existingShipmentId,
      shipment_code: null,
      from_location_id: meta.from_location_id,
      to_location_id: meta.to_location_id,
      supplier_id: meta.supplier_id,
      status: ShipmentStatus.DRAFT,
      created_by: meta.created_by,
    };

    const packagePayload: PackageDraftInput = {
      package_id: null,
      package_code: null,
      name: pkg.name,
      stone_id: pkg.stone_id,
      qty: pkg.qty,
      status: PackageStatus.DRAFT,
      created_by: meta.created_by,
      stone: pkg.stone,
    };
    if (pkg.actual_total_carat !== undefined) {
      packagePayload.actual_total_carat = pkg.actual_total_carat;
    }

    const response = yield call(createDraftPackageApi, shipmentPayload, packagePayload);
    yield put(
      createDraftPackageSuccess({
        clientId: pkg.clientId,
        shipment: response.shipment,
        package: response.package,
        labels: response.labels,
        packages_search_result: response.packages_search_result,
      }),
    );
  } catch (error: any) {
    const message = parseErrorMessage(error, 'Failed to add package');
    toast.error(message);
    yield put(createDraftPackageFailure({ clientId: pkg.clientId, error: message }));
  }
}

function* updateDraftPackageSaga(action: PayloadAction<PackageDraftInput>): Generator<any, void, any> {
  const pkg = action.payload;
  const package_id = pkg.package_id;
  if (package_id == null) {
    const message = 'Cannot update package without package_id';
    toast.error(message);
    return;
  }
  try {
    const draftShipment: RootState['stone']['newShipment']['draftShipment'] = yield select(
      (state: RootState) => state.stone.newShipment.draftShipment,
    );
    if (!draftShipment?.shipment_id) {
      throw new Error('No draft shipment to update');
    }

    // Forward the real shipment fields when populated — on the Edit page the
    // user's draft already has `from_location_id` / `to_location_id` / supplier
    // / carrier / track_number etc., and the backend will leave them unchanged.
    // Falls back to `null` (required by the Override-based type) when a field
    // genuinely isn't set yet.
    const shipmentPayload: ShipmentDraftInput = {
      shipment_id: draftShipment.shipment_id,
      shipment_code: draftShipment.shipment_code ?? null,
      from_location_id: draftShipment.from_location_id ?? null,
      to_location_id: draftShipment.to_location_id ?? null,
      supplier_id: draftShipment.supplier_id ?? null,
      carrier_code: draftShipment.carrier_code,
      track_number: draftShipment.track_number,
      status: draftShipment.status,
      created_by: draftShipment.created_by,
    };

    // Backend requires BOTH qty and actual_total_carat on every update call.
    // The hook (`useDraftShipmentItems.updatePackage`) backfills whichever one
    // the caller didn't touch; defensive fallbacks here guarantee the payload
    // is always complete even if that contract is ever broken.
    const packagePayload: PackageDraftInput = {
      package_id,
      package_code: null,
      shipment_id: draftShipment.shipment_id,
      qty: pkg.qty ?? 1,
      actual_total_carat: pkg.actual_total_carat ?? 0,
    };

    const response = yield call(updateDraftPackageApi, shipmentPayload, packagePayload);
    yield put(
      updateDraftPackageSuccess({
        package_id,
        shipment: response.shipment,
        package: response.package,
        labels: response.labels,
      }),
    );
  } catch (error: any) {
    const message = parseErrorMessage(error, 'Failed to update package');
    toast.error(message);
    yield put(updateDraftPackageFailure({ package_id, error: message }));
  }
}

function* deleteDraftPackageSaga(action: PayloadAction<{ shipment_id: number; package_id: number }>): Generator<any, void, any> {
  const { shipment_id, package_id } = action.payload;
  try {
    const response = yield call(deleteDraftPackageApi, shipment_id, package_id);
    yield put(
      deleteDraftPackageSuccess({
        package_id,
        shipment: response.shipment,
        packages_search_result: response.packages_search_result,
      }),
    );
  } catch (error: any) {
    const message = parseErrorMessage(error, 'Failed to delete package');
    toast.error(message);
    yield put(deleteDraftPackageFailure({ package_id, error: message }));
  }
}

function* clearDraftSaga(action: PayloadAction<{ shipment_id: number }>): Generator<any, void, any> {
  const { shipment_id } = action.payload;
  try {
    // Single server-side bulk DELETE — backend wipes every package on the shipment
    // in one transaction and returns the refreshed shipment (totals = 0) plus an
    // empty `packages_search_result`. Keep the shipment reference so Edit + further
    // adds on New continue to target the same shipment_id.
    const response = yield call(deleteAllDraftPackagesApi, shipment_id);
    yield put(
      clearDraftSuccess({
        shipment: response?.shipment ?? null,
        packages_search_result: response?.packages_search_result ?? null,
      }),
    );
  } catch (error: any) {
    yield handleSagaError(error, clearDraftFailure, 'Failed to clear draft');
  }
}

export default function* newShipmentSaga() {
  yield all([
    // Adds are parallel-friendly — user may click "Add" multiple times before first response. Use takeEvery.
    takeEvery(createDraftPackageRequest.type, createDraftPackageSaga),
    // Updates per-package are independent — takeEvery, so clicking two qty buttons on different rows runs both.
    takeEvery(updateDraftPackageRequest.type, updateDraftPackageSaga),
    // Deletes: takeEvery permits double-click races, but the row's delete button is disabled
    // while `mutatingPackageIds[id]` is set, so duplicate DELETEs shouldn't reach the server
    // under normal UI flow. A backend 404 on the racing call would surface via the toast.
    takeEvery(deleteDraftPackageRequest.type, deleteDraftPackageSaga),
    takeLatest(clearDraftRequest.type, clearDraftSaga),
  ]);
}
